import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { motoGearCategoryService } from "@/api/services"
import type { MotoGearCategoryResponseDto, MotoGearCategoryListParams } from "@/api/types/motoGearCategory.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, ChevronLeft, ChevronRight, Plus, X } from "lucide-react"

export default function MotoGearCategoriesListPage() {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<MotoGearCategoryResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Filters and pagination
  const [params, setParams] = useState<MotoGearCategoryListParams>({
    page: 1,
    limit: 50,
  })
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Filter states
  const [search, setSearch] = useState("")

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Form states
  const [formData, setFormData] = useState({ name: "" })
  const [saving, setSaving] = useState(false)

  // Debounce function
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      return () => {
        clearTimeout(handler)
      }
    }, [value, delay])

    return debouncedValue
  }

  // Debounced search value
  const debouncedSearch = useDebounce(search, 500)

  // Track previous debounced search to avoid unnecessary updates
  const prevDebouncedSearch = useRef<string | undefined>(undefined)

  // Auto-apply filters when search changes (skip initial mount if empty)
  useEffect(() => {
    const newSearch = debouncedSearch || undefined

    // Skip if search hasn't actually changed
    if (prevDebouncedSearch.current === newSearch) {
      return
    }

    prevDebouncedSearch.current = newSearch

    setParams((prevParams) => {
      // Only update if search actually changed
      if (prevParams.search === newSearch && prevParams.page === 1) {
        return prevParams
      }
      return {
        ...prevParams,
        page: 1,
        search: newSearch,
      }
    })
    setCurrentPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  // Load categories when params change
  useEffect(() => {
    loadCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  const loadCategories = async () => {
    setLoading(true)
    setError("")
    try {
      const response = await motoGearCategoryService.getMotoGearCategories(params)
      setCategories(response.data)
      setTotal(response.total)
      // Sync currentPage with response, but only if it matches what we requested
      const responsePage = response.page || params.page || 1
      setCurrentPage(responsePage)
      setTotalPages(response.totalPages || Math.ceil(response.total / (params.limit || 50)))
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load categories"
      setError(errorMessage)
      // On error, reset to page 1 if we're on an invalid page
      if (params.page !== undefined && params.page > totalPages && totalPages > 0) {
        setCurrentPage(1)
        setParams((prevParams) => ({ ...prevParams, page: 1 }))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setFormData({ name: "" })
    setCreateDialogOpen(true)
  }

  const handleCategoryClick = (category: MotoGearCategoryResponseDto) => {
    navigate(`/moto-gear-categories/${category.id}`)
  }

  const handleSubmitCreate = async () => {
    if (!formData.name.trim()) {
      setError("Category name is required")
      return
    }

    setSaving(true)
    setError("")
    setSuccess("")

    try {
      await motoGearCategoryService.createMotoGearCategory({ name: formData.name.trim() })
      setSuccess("Category created successfully")
      setCreateDialogOpen(false)
      setFormData({ name: "" })
      loadCategories()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to create category"
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleClearFilters = () => {
    setSearch("")
  }

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setCurrentPage(newPage)
    setParams((prevParams) => ({ ...prevParams, page: newPage }))
  }

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Moto Gear Categories</h1>
              <p className="text-muted-foreground">Manage motorcycle gear categories in the system</p>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Category
            </Button>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Input
                    id="search"
                    placeholder="Search by category name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pr-8"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {search && (
                  <p className="text-xs text-muted-foreground">
                    Searching automatically as you type...
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Categories Table */}
          <Card>
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : categories.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p>No categories found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Gears Count</TableHead>
                        <TableHead>Created At</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category) => (
                        <TableRow
                          key={category.id}
                          className="cursor-pointer hover:bg-accent/50 transition-colors"
                          onClick={() => handleCategoryClick(category)}
                        >
                          <TableCell className="font-medium">{category.name}</TableCell>
                          <TableCell>{category.motoGears?.length ?? 0}</TableCell>
                          <TableCell>
                            {new Date(category.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-muted-foreground text-sm">Click to view</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * (params.limit || 50)) + 1} to{" "}
                      {Math.min(currentPage * (params.limit || 50), total)} of {total} categories
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Gear Category</DialogTitle>
            <DialogDescription>Add a new motorcycle gear category to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Category Name</Label>
              <Input
                id="create-name"
                placeholder="e.g., Helmets"
                value={formData.name}
                onChange={(e) => setFormData({ name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitCreate()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmitCreate} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}
